import React, { useEffect, useRef, useState } from "react";
import { FiX, FiDownload, FiTrash2, FiLoader, FiImage, FiZap, FiShuffle, FiUploadCloud, FiEdit3, FiRepeat, FiInfo, FiChevronDown, FiChevronUp, FiExternalLink } from "react-icons/fi";
import { apiRequest, errorMessage } from "../api";
import { useJobHistory, formatSeconds, formatSize } from "./audioShared";

const MAX_FILE_BYTES = 10 * 1024 * 1024;
const FALLBACK = {
  styles: [["auto", "Best match"], ["photo", "Realistic photo"], ["digital", "Digital art"], ["anime", "Anime"], ["3d", "3D render"], ["cartoon", "Cartoon"], ["flat2d", "2D flat illustration"],
    ["oil", "Oil painting"], ["watercolor", "Watercolor"], ["sketch", "Pencil sketch"], ["pixel", "Pixel art"], ["logo", "Logo / flat design"], ["cinematic", "Cinematic"], ["fantasy", "Fantasy"]],
  ratios: [["1:1", "Square"], ["4:3", "Landscape 4:3"], ["16:9", "Wide 16:9"], ["3:4", "Portrait 3:4"], ["9:16", "Tall 9:16"]],
};
// The styles that turn a photo into something else - shown first when a picture is being changed.
const QUICK_EDIT_STYLES = ["cartoon", "flat2d", "3d", "anime", "sketch", "oil", "watercolor", "pixel"];
const EDIT_IDEAS = ["Make it a cartoon", "Turn it into a 3D render", "Redraw as a 2D flat illustration", "Make it a pencil sketch", "Change the background to a beach at sunset", "Make it nighttime with city lights", "Make it look like an oil painting", "Turn it into an anime character"];
const STRENGTHS = [["keep", "Keep my picture", "Changes the look but stays very close to your picture."], ["balanced", "Balanced", "A clear change that still follows your picture."], ["reimagine", "Reimagine", "Bigger changes: your picture is only a rough guide."]];

const inputClass = "w-full px-4 py-3 rounded-xl bg-navy border border-navy-border text-white text-sm placeholder:text-gray-500 focus:outline-none focus:border-accent-light";
const labelClass = "text-xs uppercase tracking-widest text-white/50 font-semibold block mb-3";
const pillOn = "bg-accent-light text-navy border-accent-light";
const pillOff = "bg-white/5 border-navy-border text-gray-300 hover:border-accent-light";
const ghost = "px-3.5 py-2 rounded-full border border-white/20 hover:border-accent-light text-xs font-medium inline-flex items-center gap-1.5 transition-colors disabled:opacity-50";

function saveBlob(filename, blob) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  setTimeout(() => URL.revokeObjectURL(url), 1500);
}

async function download(url, filename) {
  try {
    const response = await fetch(url);
    if (!response.ok) throw new Error("download failed");
    saveBlob(filename, await response.blob());
  } catch {
    window.open(url, "_blank", "noopener");   // the browser downloads it itself
  }
}

const slug = (text) => (text || "image").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 40) || "image";

// Text to Image Generation: describe a picture and get it drawn - or upload a picture and change it (cartoon, 2D, 3D, a new background...).
// The work runs on the server in the background and this page polls for the result.
export default function ImageGeneratorTool() {
  const { clientId, history, loading, running, latest, elapsed, add, remove } = useJobHistory("image");
  const [options, setOptions] = useState(null);
  const [mode, setMode] = useState("text");               // "text" | "edit"
  const [prompt, setPrompt] = useState("");
  const [negative, setNegative] = useState("");
  const [style, setStyle] = useState("auto");
  const [ratio, setRatio] = useState("1:1");
  const [count, setCount] = useState(1);
  const [seed, setSeed] = useState("");
  const [strength, setStrength] = useState("balanced");
  const [file, setFile] = useState(null);                  // { file, url }
  const [dragging, setDragging] = useState(false);
  const [advanced, setAdvanced] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [improving, setImproving] = useState(false);
  const [error, setError] = useState("");
  const [fieldError, setFieldError] = useState("");
  const [viewId, setViewId] = useState(null);
  const [detail, setDetail] = useState(null);
  const inputRef = useRef(null);
  const resultRef = useRef(null);

  const styles = options ? options.styles.map((s) => [s.key, s.label]) : FALLBACK.styles;
  const ratios = options ? options.ratios.map((r) => [r.key, r.label]) : FALLBACK.ratios;
  const maxPrompt = options?.max_prompt || 500;
  const maxImages = options?.max_images || 4;
  const ideas = options?.ideas || [];
  const editing = mode === "edit";
  const shownStyles = editing ? [...QUICK_EDIT_STYLES.map((k) => styles.find(([key]) => key === k)).filter(Boolean), ...styles.filter(([k]) => k === "auto" || !QUICK_EDIT_STYLES.includes(k))] : styles;

  const canSubmit = !submitting && !running && (editing ? !!file && (prompt.trim().length >= 3 || style !== "auto") : prompt.trim().length >= 3);

  useEffect(() => {
    let alive = true;
    apiRequest("/core/image/options/").then(({ ok, data }) => { if (alive && ok) setOptions(data); });
    return () => { alive = false; };
  }, []);

  // Show the newest finished job automatically.
  useEffect(() => {
    if (latest && latest.status === "completed" && latest.images?.length) setViewId(latest.id);
  }, [latest?.id, latest?.status]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!viewId) { setDetail(null); return undefined; }
    let alive = true;
    apiRequest(`/core/voice-clone/${viewId}/?client_id=${encodeURIComponent(clientId)}`).then(({ ok, data }) => { if (alive) setDetail(ok ? data : null); });
    return () => { alive = false; };
  }, [viewId, clientId]);

  const pickFile = (picked) => {
    setFieldError("");
    if (!picked) return;
    if (!picked.type.startsWith("image/")) return setFieldError("Choose a JPG, PNG or WebP picture.");
    if (picked.size > MAX_FILE_BYTES) return setFieldError("This picture is larger than 10 MB.");
    if (file?.url) URL.revokeObjectURL(file.url);
    setFile({ file: picked, url: URL.createObjectURL(picked) });
  };

  const submit = async (override) => {
    setError("");
    setFieldError("");
    setSubmitting(true);
    const values = { prompt, negative, style, ratio, count, seed, strength, ...override };
    const form = new FormData();
    form.append("client_id", clientId);
    form.append("kind", "image");
    form.append("prompt", values.prompt);
    form.append("negative", values.negative);
    form.append("style", values.style);
    form.append("ratio", values.ratio);
    form.append("count", String(values.count));
    form.append("seed", String(values.seed || ""));
    form.append("strength", values.strength);
    const source = override?.file !== undefined ? override.file : file?.file;
    if (editing && source) form.append("original", source);
    const { ok, data } = await apiRequest("/core/voice-clone/", { method: "POST", body: form });
    setSubmitting(false);
    if (!ok) {
      if (data?.prompt || data?.original) return setFieldError(data.prompt || data.original);
      return setError(errorMessage(data));
    }
    setViewId(null);
    add(data);
    setTimeout(() => resultRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
  };

  const improve = async () => {
    setFieldError("");
    setImproving(true);
    const { ok, data } = await apiRequest("/core/image/enhance/", { method: "POST", body: { prompt, style } });
    setImproving(false);
    if (!ok) return setFieldError(data?.prompt || errorMessage(data));
    setPrompt(data.prompt.slice(0, maxPrompt));
  };

  const surprise = () => {
    if (!ideas.length) return;
    setMode("text");
    setPrompt(ideas[Math.floor(Math.random() * ideas.length)]);
  };

  // Gets a finished picture back as a file, to change it again.
  const editResult = async (image) => {
    try {
      const blob = await (await fetch(image.url)).blob();
      pickFile(new File([blob], "picture.png", { type: blob.type || "image/png" }));
      setMode("edit");
      setPrompt("");
      setStyle("auto");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch {
      setError("The picture could not be loaded again. Download it and upload it instead.");
    }
  };

  const reuse = (job) => {
    let stored = {};
    try { stored = JSON.parse(job.options || "{}"); } catch { /* old job */ }
    if (!stored.edit) {
      setMode("text");
      setPrompt(stored.prompt || "");
      setStyle(stored.style || "auto");
      setRatio(stored.ratio || "1:1");
      setNegative(stored.negative || "");
      setCount(stored.count || 1);
    }
  };

  const view = detail && detail.status === "completed" ? detail : null;
  const stored = (() => { try { return JSON.parse(view?.options || "{}"); } catch { return {}; } })();
  const extra = view?.segments && !Array.isArray(view.segments) ? view.segments : {};
  const styleLabel = (key) => styles.find(([k]) => k === key)?.[1] || key;

  return (
    <div className="max-w-5xl mx-auto px-6 md:px-12 py-14">
      <h2 className="text-2xl md:text-3xl font-bold text-center mb-3">Text to Image Generation</h2>
      <p className="text-center text-gray-400 max-w-2xl mx-auto mb-10">
        Describe a picture and get it drawn - in any language. Or upload your own picture and change it: turn a photo into a cartoon, a 2D illustration or a 3D render,
        or change what is in it, just by writing what you want.
      </p>

      <div className="flex justify-center mb-6">
        <div className="inline-flex rounded-full bg-white/5 border border-navy-border p-1">
          {[["text", "Create from text", FiImage], ["edit", "Change my picture", FiEdit3]].map(([key, label, Icon]) => (
            <button key={key} type="button" onClick={() => { setMode(key); setFieldError(""); }}
              className={`px-5 py-2 rounded-full text-sm font-semibold inline-flex items-center gap-2 transition-colors ${mode === key ? "bg-brand-gradient text-white shadow-md shadow-accent/20" : "text-gray-300 hover:text-white"}`}>
              <Icon size={15} /> {label}
            </button>
          ))}
        </div>
      </div>

      <div className="rounded-2xl bg-white/5 border border-navy-border p-6 space-y-6">
        {editing && (
          <div>
            <span className={labelClass}>Your picture</span>
            {!file ? (
              <div
                onClick={() => inputRef.current?.click()}
                onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
                onDragLeave={() => setDragging(false)}
                onDrop={(e) => { e.preventDefault(); setDragging(false); pickFile(e.dataTransfer.files?.[0]); }}
                role="button" tabIndex={0}
                onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && inputRef.current?.click()}
                className={`cursor-pointer rounded-xl border-2 border-dashed px-4 py-10 text-center transition-colors ${dragging ? "border-accent-light bg-accent/10" : "border-navy-border hover:border-accent-light/70"}`}
              >
                <FiUploadCloud className="mx-auto text-accent-light" size={30} />
                <p className="mt-3 text-sm text-gray-200">Click to choose a picture, or drop it here</p>
                <p className="text-xs text-gray-500 mt-1">JPG, PNG or WebP - up to 10 MB</p>
              </div>
            ) : (
              <div className="flex items-center gap-4 rounded-xl bg-navy border border-navy-border p-3">
                <img src={file.url} alt="Upload preview" className="w-24 h-24 object-cover rounded-lg" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium truncate">{file.file.name}</p>
                  <p className="text-xs text-gray-500">{formatSize(file.file.size)}</p>
                  <button type="button" onClick={() => inputRef.current?.click()} className="text-xs text-accent-light hover:underline mt-1">Choose another</button>
                </div>
                <button type="button" onClick={() => { URL.revokeObjectURL(file.url); setFile(null); }} aria-label="Remove picture" className="text-gray-400 hover:text-red-400 shrink-0"><FiX size={18} /></button>
              </div>
            )}
            <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={(e) => { pickFile(e.target.files?.[0]); e.target.value = ""; }} />
          </div>
        )}

        <div>
          <div className="flex items-center justify-between mb-3">
            <label htmlFor="image-prompt" className="text-xs uppercase tracking-widest text-white/50 font-semibold">
              {editing ? "What should change?" : "Describe your picture"}
              {editing && <span className="normal-case tracking-normal text-gray-500 font-normal"> (optional if you choose a style below)</span>}
            </label>
            <span className="text-xs text-gray-500">{prompt.length} / {maxPrompt}</span>
          </div>
          <textarea
            id="image-prompt"
            value={prompt}
            onChange={(e) => setPrompt(e.target.value.slice(0, maxPrompt))}
            placeholder={editing ? "e.g. Make it a cartoon with bold outlines, or: change the background to a beach at sunset" : "e.g. A red fox sitting in a snowy forest at sunrise, soft golden light"}
            dir="auto"
            className={`${inputClass} min-h-[110px] leading-relaxed text-base`}
          />
          <div className="flex flex-wrap gap-2 mt-3">
            {editing ? EDIT_IDEAS.slice(0, 6).map((idea) => (
              <button key={idea} type="button" onClick={() => setPrompt(idea)} className="px-3 py-1 rounded-full text-xs border border-navy-border text-gray-300 hover:border-accent-light">{idea}</button>
            )) : (
              <>
                <button type="button" onClick={surprise} className={ghost}><FiShuffle size={13} /> Surprise me</button>
                <button type="button" onClick={improve} disabled={improving || prompt.trim().length < 3} className={ghost}>
                  {improving ? <><FiLoader className="animate-spin" size={13} /> Improving...</> : <><FiZap size={13} /> Improve my prompt</>}
                </button>
              </>
            )}
          </div>
          {fieldError && <p className="text-sm text-amber-300 mt-3">{fieldError}</p>}
          {!editing && <p className="text-xs text-gray-500 mt-3">Write in English, Urdu, Arabic, Hindi... any language: it is translated for the image model. Be specific about the subject, setting, light and mood.</p>}
        </div>

        <div>
          <span className={labelClass}>{editing ? "Style to turn it into" : "Style"}</span>
          <div className="flex flex-wrap gap-2">
            {shownStyles.map(([key, label]) => (
              <button key={key} type="button" onClick={() => setStyle(key)} aria-pressed={style === key}
                className={`px-3.5 py-1.5 rounded-full text-sm font-semibold border transition-colors ${style === key ? pillOn : pillOff}`}>{label}</button>
            ))}
          </div>
        </div>

        {editing ? (
          <div>
            <span className={labelClass}>How much may change</span>
            <div className="grid sm:grid-cols-3 gap-3">
              {STRENGTHS.map(([key, label, hint]) => (
                <button key={key} type="button" onClick={() => setStrength(key)} aria-pressed={strength === key}
                  className={`text-start rounded-xl border p-3.5 transition-colors ${strength === key ? "border-accent-light bg-accent/10" : "border-navy-border bg-white/5 hover:border-accent-light/60"}`}>
                  <p className="text-sm font-semibold">{label}</p>
                  <p className="text-xs text-gray-400 mt-1">{hint}</p>
                </button>
              ))}
            </div>
            <p className="text-xs text-gray-500 mt-3">The new picture has the same shape as yours.</p>
          </div>
        ) : (
          <div>
            <span className={labelClass}>Shape</span>
            <div className="flex flex-wrap gap-3">
              {ratios.map(([key, label]) => {
                const [w, h] = key.split(":").map(Number);
                const scale = 30 / Math.max(w, h);
                return (
                  <button key={key} type="button" onClick={() => setRatio(key)} aria-pressed={ratio === key} title={label}
                    className={`px-3.5 py-2 rounded-xl border text-xs font-semibold inline-flex items-center gap-2.5 transition-colors ${ratio === key ? pillOn : pillOff}`}>
                    <span className="inline-block rounded-sm border-2 border-current" style={{ width: w * scale, height: h * scale }} />
                    {label}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <div className="flex flex-wrap items-end justify-between gap-5">
          <div>
            <span className={labelClass}>How many pictures</span>
            <div className="flex gap-2">
              {Array.from({ length: maxImages }, (_, i) => i + 1).map((n) => (
                <button key={n} type="button" onClick={() => setCount(n)} aria-pressed={count === n}
                  className={`w-11 h-10 rounded-full text-sm font-semibold border transition-colors ${count === n ? pillOn : pillOff}`}>{n}</button>
              ))}
            </div>
          </div>
          <button type="button" onClick={() => setAdvanced((v) => !v)} className="text-sm text-gray-400 hover:text-white inline-flex items-center gap-1.5">
            {advanced ? <FiChevronUp /> : <FiChevronDown />} More options
          </button>
        </div>

        {advanced && (
          <div className="grid sm:grid-cols-2 gap-4 border-t border-navy-border pt-5">
            <div>
              <label htmlFor="image-negative" className={labelClass}>Things to avoid (optional)</label>
              <input id="image-negative" className={inputClass} value={negative} onChange={(e) => setNegative(e.target.value.slice(0, 200))} placeholder="e.g. text, people, blurry background" dir="auto" />
            </div>
            <div>
              <label htmlFor="image-seed" className={labelClass}>Seed (optional)</label>
              <input id="image-seed" className={inputClass} value={seed} onChange={(e) => setSeed(e.target.value.replace(/\D/g, "").slice(0, 10))} placeholder="Empty = a different picture every time" inputMode="numeric" />
              <p className="text-xs text-gray-500 mt-1">The same description and seed give the same picture again.</p>
            </div>
          </div>
        )}
      </div>

      {error && <p className="text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded-xl px-5 py-4 mt-6">{error}</p>}

      <button
        onClick={() => submit()}
        disabled={!canSubmit}
        className="w-full mt-6 flex items-center justify-center gap-2 px-6 py-4 rounded-full bg-brand-gradient text-white font-semibold text-base shadow-lg shadow-accent/20 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {submitting ? <><FiLoader className="animate-spin" /> Sending...</> : running ? <><FiLoader className="animate-spin" /> Drawing...</> : editing ? <><FiEdit3 /> Change my picture</> : <><FiImage /> Generate {count > 1 ? `${count} pictures` : "picture"}</>}
      </button>
      {!canSubmit && !running && !submitting && (
        <p className="text-xs text-gray-500 text-center mt-2">{editing && !file ? "Add your picture first" : editing ? "Say what to change, or pick a style" : "Describe your picture first"}</p>
      )}
      <p className="text-xs text-gray-500 text-center mt-3">Free image services draw your pictures. Your description {editing ? "and picture are" : "is"} sent to them, so do not use private or confidential {editing ? "pictures" : "text"}.
        Sexual content, graphic violence and requests to undress people are refused.</p>

      <div ref={resultRef} />
      {running && (
        <div className="rounded-2xl bg-white/5 border border-accent-light/30 p-6 mt-6 flex items-center gap-3">
          <FiLoader className="animate-spin text-accent-light shrink-0" size={22} />
          <div>
            <p className="font-semibold">
              {running.options && JSON.parse(running.options || "{}").edit ? "Changing your picture" : "Drawing"}... {formatSeconds(elapsed)}
              {running.progress && <span className="text-gray-400 font-normal"> · {running.progress}</span>}
            </p>
            <p className="text-sm text-gray-400">Usually 10-40 seconds. When the free services are busy you wait in a queue, which can take a few minutes - you can leave this page open.</p>
          </div>
        </div>
      )}

      {latest && latest.status === "failed" && !running && (
        <div className="rounded-2xl bg-red-500/10 border border-red-500/30 p-5 mt-6 text-sm text-red-200 flex items-start gap-3">
          <FiInfo className="mt-0.5 shrink-0" />
          <span>{latest.error_message || "The picture could not be made. Please try again."}</span>
        </div>
      )}

      {view && !running && (
        <div className="rounded-2xl bg-white/5 border border-accent-light/30 p-6 mt-6">
          <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
            <div className="min-w-0">
              <p className="text-xs uppercase tracking-widest text-white/50 font-semibold mb-1">{extra.edit ? "Your picture, changed" : "Your pictures"} · {styleLabel(stored.style)}</p>
              <p className="font-semibold">{view.original_name}</p>
            </div>
            {view.engine && <span className="px-3 py-1 rounded-full text-xs font-semibold bg-white/10 text-gray-200">{view.engine}</span>}
          </div>

          {extra.notice && <p className="text-sm text-amber-200 bg-amber-500/10 border border-amber-500/30 rounded-xl px-4 py-3 mb-4">{extra.notice}</p>}

          <div className={`grid gap-4 ${view.images.length + (view.source_image ? 1 : 0) > 2 ? "sm:grid-cols-2" : view.images.length + (view.source_image ? 1 : 0) === 2 ? "sm:grid-cols-2" : ""}`}>
            {view.source_image && (
              <figure className="rounded-xl overflow-hidden bg-navy border border-navy-border">
                <img src={view.source_image} alt="Original upload" className="w-full object-contain bg-black/30 max-h-[520px]" />
                <figcaption className="px-4 py-3 text-xs text-gray-400">Your original</figcaption>
              </figure>
            )}
            {view.images.map((image, i) => (
              <figure key={image.url} className="rounded-xl overflow-hidden bg-navy border border-navy-border">
                <a href={image.url} target="_blank" rel="noreferrer"><img src={image.url} alt={`Result ${i + 1}: ${view.original_name}`} className="w-full object-contain bg-black/30 max-h-[620px]" loading="lazy" /></a>
                <figcaption className="px-4 py-3 flex flex-wrap items-center gap-2">
                  <button type="button" onClick={() => download(image.url, `${slug(view.original_name)}-${i + 1}.png`)} className={ghost}><FiDownload size={13} /> Download PNG</button>
                  <button type="button" onClick={() => editResult(image)} className={ghost}><FiEdit3 size={13} /> Change this</button>
                  <a href={image.url} target="_blank" rel="noreferrer" className={ghost}><FiExternalLink size={13} /> Open</a>
                  {image.seed != null && <span className="text-[11px] text-gray-500 ms-auto">seed {image.seed}</span>}
                </figcaption>
              </figure>
            ))}
          </div>

          <div className="flex flex-wrap gap-3 mt-5">
            {!extra.edit && (
              <button type="button" onClick={() => { reuse(view); submit({ prompt: stored.prompt, negative: stored.negative || "", style: stored.style, ratio: stored.ratio, count: stored.count || 1, seed: "" }); }} disabled={submitting || !!running} className={ghost}>
                <FiRepeat size={13} /> Make new versions
              </button>
            )}
            {!extra.edit && <button type="button" onClick={() => { reuse(view); window.scrollTo({ top: 0, behavior: "smooth" }); }} className={ghost}><FiEdit3 size={13} /> Adjust the description</button>}
          </div>
          {view.transcript && (
            <details className="mt-4 text-xs text-gray-400">
              <summary className="cursor-pointer hover:text-white">The exact prompt that was used</summary>
              <p className="mt-2 leading-relaxed" dir="auto">{view.transcript}</p>
              {extra.translated && <p className="mt-2 text-gray-500">Translated from your description: {extra.translated}</p>}
            </details>
          )}
          <p className="text-xs text-gray-500 mt-4">AI pictures can contain mistakes (hands, text, small details). Check them before you use them, and do not present them as photographs of real events.</p>
        </div>
      )}

      <div className="mt-10">
        <p className="text-xs uppercase tracking-widest text-white/50 font-semibold mb-4">Your pictures</p>
        {loading ? (
          <p className="text-sm text-gray-400">Loading...</p>
        ) : history.length === 0 ? (
          <p className="text-sm text-gray-500">Nothing here yet. Your pictures will appear here.</p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {history.map((job) => (
              <div key={job.id} className={`rounded-xl bg-white/5 border overflow-hidden ${viewId === job.id ? "border-accent-light/60" : "border-navy-border"}`}>
                {job.status === "completed" && job.images?.length ? (
                  <button type="button" onClick={() => setViewId(job.id)} className="block w-full" aria-label={`Show ${job.original_name}`}>
                    <img src={job.images[0].thumb} alt={job.original_name} className="w-full aspect-square object-cover" loading="lazy" />
                  </button>
                ) : (
                  <div className="w-full aspect-square flex items-center justify-center text-xs text-gray-500 px-3 text-center bg-navy">
                    {job.status === "failed" ? <span className="text-red-300">{job.error_message}</span> : <span className="inline-flex items-center gap-2"><FiLoader className="animate-spin" /> Drawing...</span>}
                  </div>
                )}
                <div className="p-3">
                  <p className="text-xs text-gray-200 line-clamp-2 min-h-[2rem]" dir="auto">{job.original_name}</p>
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-[11px] text-gray-500">{job.images?.length > 1 ? `${job.images.length} pictures` : new Date(job.created_at).toLocaleDateString()}</span>
                    {job.status !== "processing" && job.status !== "queued" && (
                      <button onClick={() => { if (viewId === job.id) setViewId(null); remove(job); }} title="Delete" className="text-gray-400 hover:text-red-400"><FiTrash2 size={14} /></button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
